/**
 * An agent's Drive tool waiting, inside a running turn, for more Google
 * access — the chat and the meta-agent panel show the same card.
 *
 * The backend publishes `google_access_needed` and, when the wait ends,
 * `google_access_resolved` (cloud_storage.services.google_access); the turn
 * itself carries on once the person allows it, so the card only asks.
 */

import { useCallback, useEffect, useState } from 'react';
import { GoogleAccessPrompt } from './GoogleAccessPrompt';

type Access = 'read' | 'edit';

export interface TurnGoogleAccess {
  pending: Access | null;
  /** Feed every stream event; true when it was one of the two access events. */
  onStreamEvent: (event: { type?: string; access?: string } | null | undefined) => boolean;
  dismiss: () => void;
}

/** `active`: a turn is streaming — the card never outlives it. */
export function useTurnGoogleAccess(active: boolean): TurnGoogleAccess {
  const [pending, setPending] = useState<Access | null>(null);
  useEffect(() => { if (!active) setPending(null); }, [active]);

  const onStreamEvent = useCallback((event: { type?: string; access?: string } | null | undefined) => {
    if (event?.type === 'google_access_needed') {
      setPending(event.access === 'edit' ? 'edit' : 'read');
      return true;
    }
    if (event?.type === 'google_access_resolved') {
      // Only the level this card asked for: another tool's wait (a different
      // level) ending does not answer this one.
      const resolved = event.access;
      setPending((prev) => (prev === resolved ? null : prev));
      return true;
    }
    return false;
  }, []);

  const dismiss = useCallback(() => setPending(null), []);
  return { pending, onStreamEvent, dismiss };
}

export function TurnGoogleAccessCard({ access, onDismiss }: { access: Access; onDismiss: () => void }) {
  return (
    <GoogleAccessPrompt
      variant="card"
      access={access}
      reason={access === 'edit' ? 'agentEdit' : 'agentRead'}
      onDeclined={onDismiss}
      reportDecline
    />
  );
}
